// React, services, context and routing
import { useAbility } from "@casl/react";
import { AbilityContext } from "Can";
import { AuthContext } from "context";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import crudsService from "services/cruds-service";
import productService from "services/product-service";

// E4A React components
import Footer from "e4acomponents/Footer";
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import DataTable from "e4acomponents/Tables/DataTable";

// Material Dashboard and MUI components
import EditTwoToneIcon from "@mui/icons-material/EditTwoTone";
import VisibilityTwoToneIcon from "@mui/icons-material/VisibilityTwoTone";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Fulfillment center components
import FiltersDialog from "../components/filters-dialog";
import { convertDate } from "../components/helperFunctions";

export default function DispatchedProducts() {
    // State management
    const [isLoading, setIsLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [user, setUser] = useState({});
    const [organization, setOrganization] = useState({});
    const [tableData, setTableData] = useState([]);
    const [showFiltersDialog, setShowFiltersDialog] = useState(false);

    // Contexts and routing
    const authContext = useContext(AuthContext);
    const ability = useAbility(AbilityContext);
    const navigate = useNavigate();

    // Fetch products 
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                //  Fetch current user's info
                const userId = await authContext.getCurrentUser();
                const currentUser = await crudsService.getUser(userId);
                setUser(currentUser.data);

                let products = [];
                const org = currentUser.data.relationships.organizations;
                if (org) { // If user has an organization associated
                    setOrganization(currentUser.data.relationships.organizations.data[0]);
                    const productsResponse = await productService.getOrgProducts(org.data[0].id);
                    products = productsResponse.data;

                } else { // If user has no organization associated
                    const productsResponse = await productService.getAllProducts();
                    products = productsResponse.data;

                }
                setProducts(products);

            } catch (error) {
                console.error("Error fetching products: ", error);
            } finally {
                setIsLoading(false);
            }
        })();
    }, []);

    // Update table's rows at the slightest change
    useEffect(() => {
        setTableData(getRows(products));
    }, [products]);

    // Formats data for the DataTable
    const getRows = (info) => {
        let updatedInfo = (info || [])
            .filter((e) => String(e?.status || "").toLowerCase() === "dispatched")
            .map((e) => {
                return {
                    id: e._id,
                    serialNumber: e.serialNumber,
                    firmwareVersion: e.firmwareVersion,
                    dateOfStorage: convertDate(e.dateOfStorage),
                    shelfLife: `${e.shelfLife[0]} ${e.shelfLife[1]}`,
                    organizationName: e.organization?.name || "",
                    statusChip: statusChip(e.status),
                    status: e.status,
                }
            });
        return updatedInfo;
    }

    // DataTable configuration
    const dataTableData = {
        columns: [
            { Header: "Serial Number", accessor: "serialNumber" },
            { Header: "Firmware Version", accessor: "firmwareVersion" },
            { Header: "Date of Storage", accessor: "dateOfStorage" },
            { Header: "Shelf Life", accessor: "shelfLife" },
            ...(!organization?.id ?
                [{
                    Header: "Organization",
                    accessor: "organizationName"
                }] : []
            ),
            { Header: "Status", accessor: "status", Cell: ({ row }) => row.original.statusChip },
            {
                Header: "Actions",
                accessor: "actions",
                disableSortBy: true,
                Cell: ({ row }) => {
                    return (
                        <MDBox display="flex" alignItems="center">
                            {ability.can('access', 'edit-product') && (
                                <Tooltip title="Edit Product">
                                    <IconButton
                                        onClick={() =>
                                            navigate(`/fulfillment-center/edit-product/${row.original.id}`)}
                                    >
                                        <EditTwoToneIcon color="success" />
                                    </IconButton>
                                </Tooltip>
                            )}

                            <Tooltip title="View Product">
                                <IconButton
                                    onClick={() =>
                                        navigate(`/fulfillment-center/view-product/${row.original.id}`)
                                    }>
                                    <VisibilityTwoToneIcon color="info" />
                                </IconButton>
                            </Tooltip>
                        </MDBox>
                    )
                }
            }
        ],
        rows: tableData,
    }

    // Opens the filters dialog
    const openFiltersDialog = () => {
        setShowFiltersDialog(true);
    }
    // Closes the filters dialog
    const closeFiltersDialog = () => {
        setShowFiltersDialog(false);
    }

    // Applies the dialogs filters to the dataTable
    const applyFilters = (filters) => {
        setShowFiltersDialog(false);
        setTableData(
            getRows(
                products.
                    filter((product) => product.organization._id ?
                        filters.includes(product.organization._id) : false
                    )
            ));
    }

    // Renders a status chip based on the product's status
    const statusChip = (status) => {
        const normalized = String(status || "").toLowerCase();

        const map = {
            stored: { color: "info" },
            requested: { color: "warning" },
            scheduled: { color: "success" },
            "dispatch canceled": { color: "error" },
            dispatched: { color: "success" },
        };
        const variant = map[normalized];
        if (!variant) return null;
        return (
            <Chip variant="outlined" size="small" label={status} color={variant.color} />
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>

                {/* Dialog for filtering by organization */}
                <FiltersDialog
                    handleClose={closeFiltersDialog}
                    open={showFiltersDialog}
                    submit={applyFilters}
                />

                <Card>
                    <MDBox p={3} lineHeight={1} display="flex" justifyContent="space-between">

                        {/* Title */}
                        <MDTypography variant="h5" fontWeight="medium">
                            {!(organization?.id) ?
                                "Client products" :
                                `Your Products (${organization?.label})`}
                        </MDTypography>

                        {/* Add New Product Button */}
                        {ability.can('access', 'add-product') && (
                            <MDButton
                                variant="gradient"
                                color="info"
                                size="small"
                                type="submit"
                                onClick={() => navigate("/fulfillment-center/add-product")}
                            >
                                Add Product
                            </MDButton>
                        )}

                    </MDBox>
                    {isLoading ? (
                        // Loading Spinner
                        <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                            <CircularProgress />
                        </MDBox>

                    ) : (
                        <>
                            {/* DataTable */}
                            <MDBox
                                sx={{
                                    "& table": {
                                        "& tbody tr": {
                                            transition: "background-color 0.2s ease",
                                        },
                                        // Highlight rows that contain a checked checkbox, regardless of order
                                        "& tbody tr:has(input[type='checkbox']:checked)": {
                                            backgroundColor: "#e3f2fd !important",
                                            "&:hover": {
                                                backgroundColor: "#bbdefb !important",
                                            },
                                        },
                                        // Default hover for non-selected rows
                                        "& tbody tr:not(:has(input[type='checkbox']:checked')):hover": {
                                            backgroundColor: "#f5f5f5 !important",
                                        },
                                    },
                                }}
                            >
                                {organization?.id ?
                                    (
                                        <DataTable
                                            table={dataTableData}
                                            canSearch={true}
                                            tableId="dispatched-products-table-1"
                                        />
                                    ) : (
                                        <DataTable
                                            table={dataTableData}
                                            canSearch={true}
                                            hasFilters
                                            openFiltersModal={openFiltersDialog}
                                            tableId="dispatched-products-table-2"
                                        />
                                    )}
                            </MDBox>
                        </>
                    )}
                </Card>

            </MDBox >
            <Footer />
        </DashboardLayout >
    );
}